import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { TitleCommitmentService } from '../service/titleCommitmentService';
import { LienFormStore } from './lienFormStore';
import { LienResolutionStore } from './lienResolutionStore';
import { LienRow, TaxRecordRow, FieldReviewRow, LoanPropertyOption, TITLE_COMMITMENT_LIEN_SOURCE } from '../types';
import { TaxFormStore } from './taxFormStore';
import { DocReviewStoreV2 } from '@roc/feature-ai-docs-review';

const PROCESSING_STATUS_TEXT: Record<string, string> = {
  IN_PROGRESS: 'Processing - This may take a few seconds. Click Refresh to check for results.',
  SUCCESS: 'Document processed successfully',
  FAILED: 'Processing failed — please try again',
};

export class TitleCommitmentStore {
  globalStore: GlobalStore;
  private titleCommitmentService: TitleCommitmentService;
  private docReviewStoreV2: DocReviewStoreV2;

  loanId: number | null;
  taskId: number | null;
  liens: LienRow[];
  loanProperties: LoanPropertyOption[];
  processingStatus: string;
  errorMessage: string | null;
  lienFormStore: LienFormStore;
  lienResolutionStore: LienResolutionStore;
  taxFormStore: TaxFormStore;

  constructor(globalStore: GlobalStore, docReviewStoreV2: DocReviewStoreV2) {
    this.globalStore = globalStore;
    this.docReviewStoreV2 = docReviewStoreV2;
    this.titleCommitmentService = new TitleCommitmentService();
    this.loanId = null;
    this.taskId = null;
    this.liens = [];
    this.loanProperties = [];
    this.processingStatus = null;
    this.errorMessage = null;
    this.lienFormStore = new LienFormStore(globalStore);
    this.lienResolutionStore = new LienResolutionStore(globalStore);
    this.taxFormStore = new TaxFormStore(globalStore);

    makeObservable(this, {
      loanId: observable,
      taskId: observable,
      document: computed,
      taxRecords: computed,
      fieldReviewItems: computed,
      statusText: computed,
      liens: observable,
      loanProperties: observable,
      processingStatus: observable,
      errorMessage: observable,
      getLienById: action,
      getReviewTaskData: flow,
      saveLien: flow,
      saveTaxRecord: flow,
      loadForm: flow,
      loadTaxForm: action,
      deleteLienDocument: flow,
      createResolution: flow,
      refetchReviewTaskData: flow
    });
  }

  get document() {
    return this.docReviewStoreV2.document;
  }

  get statusText(): string {
    if (this.processingStatus === 'FLAGGED') {
      return this.errorMessage || PROCESSING_STATUS_TEXT.FAILED;
    }
    return PROCESSING_STATUS_TEXT[this.processingStatus] ?? '';
  }

  get taxRecords(): TaxRecordRow[] {
    const records = this.docReviewStoreV2.docReviewData?.taxRecords;
    return Array.isArray(records) ? records : [];
  }

  get fieldReviewItems(): FieldReviewRow[] {
    const items = this.docReviewStoreV2.docReviewData?.items;
    return Array.isArray(items) ? items : [];
  }

  *getReviewTaskData(taskId: number) {
    this.taskId = taskId;
    try {
      const response = yield this.titleCommitmentService.getScreenData(taskId);
      const data = response?.data;
      if (!data) return;
      const task = data.task;
      this.docReviewStoreV2.setDocument(
        task ? { ...task, taskName: task.taskCode, dropboxPath: task.path } : null
      );
      this.docReviewStoreV2.setDocReviewData(
        data.docReviewData ? JSON.parse(data.docReviewData) : {}
      );
      this.docReviewStoreV2.setProcessingStatus(data.processingStatus ?? null);
      this.loanId = data.loanId ?? this.document?.loanId ?? null;
      this.liens = data.liens ?? [];
      this.loanProperties = Array.isArray(data.loanProperties)
        ? data.loanProperties.map(p => ({
            propertyId: p.propertyId,
            fullAddress: p.fullAddress,
          }))
        : [];
      this.processingStatus = data.processingStatus ?? null;
      this.errorMessage = data.errorMessage ?? null;
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the title commitment data',
      });
    }
  }

  *refetchReviewTaskData(taskId: number) {
    yield this.getReviewTaskData(taskId);
    this.processingStatus = 'IN_PROGRESS';
  }

  *saveLien(editingLienId?: number) {
    try {
      this.lienFormStore.runFormValidation();
      if (!this.lienFormStore.form.meta.isValid) {
        throw new Error();
      }
    } catch (e) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fix the validation errors',
      });
      throw e;
    }

    try {
      const { originalAmount, madeTo, datedDate, recordedDate, madeBy, lienType, propertyId } = this.lienFormStore.getFormValues();
      const existingLien = this.getLienById(editingLienId);
      // Collateral assignment goes through saveLien itself (the backend validates the property
      // belongs to the loan and persists property_id), so it works for Add as well as Edit. A
      // single-property loan has no dropdown and always resolves to its only property.
      const selectedPropertyId = propertyId ? Number(propertyId) : null;
      const resolvedPropertyId =
        this.loanProperties.length === 1
          ? this.loanProperties[0].propertyId
          : selectedPropertyId ?? existingLien?.propertyId ?? null;
      const lienRow: LienRow = {
        ...existingLien,
        originalAmount,
        madeTo,
        datedDate,
        recordedDate,
        madeBy,
        lienType,
        loanId: this.loanId,
        propertyId: resolvedPropertyId,
        sourceType: TITLE_COMMITMENT_LIEN_SOURCE,
      };
      delete lienRow.status;
      yield this.titleCommitmentService.saveLien(lienRow);

      yield this.getReviewTaskData(this.taskId);
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the lien',
      });
      throw new Error();
    }
  }

  getLienById(lienId: number): LienRow | undefined {
    return this.liens.find(l => l.propertyLienId === lienId);
  }

  *loadForm(lienId: number, onOpen: (lienId?: number) => void) {
    this.lienFormStore.reset();
    const lien = this.getLienById(lienId);
    this.lienFormStore.loadForm({
      originalAmount: lien?.originalAmount,
      madeTo: lien?.madeTo,
      datedDate: lien?.datedDate,
      recordedDate: lien?.recordedDate,
      madeBy: lien?.madeBy,
      lienType: lien?.lienType,
      propertyId: lien?.propertyId != null ? String(lien.propertyId) : '',
    });
    onOpen(lienId);
  }

  *deleteLienDocument(taskId: number) {
    try {
      yield this.titleCommitmentService.deleteLienDocument(taskId);
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while deleting the document.',
      });
    }
  }

  *createResolution(propertyLienId: number) {
    try {
      yield this.titleCommitmentService.createResolution(propertyLienId);
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while creating the resolution.',
      });
      throw new Error();
    }
  }

  loadTaxForm(
    record: TaxRecordRow,
    index: number,
    onOpen: (index: number) => void
  ) {
    this.taxFormStore.reset();
    const trackedProperty =
      record.propertyId != null
        ? this.loanProperties.find(p => p.propertyId === record.propertyId)
        : undefined;
    const addressMatch = this.loanProperties.find(
      p => p.fullAddress === record.propertyAddress
    );
    const defaultPropertyId = trackedProperty
      ? String(trackedProperty.propertyId)
      : this.loanProperties.length === 1
      ? String(this.loanProperties[0].propertyId)
      : addressMatch
      ? String(addressMatch.propertyId)
      : '';
    this.taxFormStore.loadForm({
      annualTaxAmount: record.annualTaxAmount ?? '',
      propertyId: defaultPropertyId,
    });
    onOpen(index);
  }

  *saveTaxRecord(recordIndex: number) {
    try {
      this.taxFormStore.runFormValidation();
      if (!this.taxFormStore.form.meta.isValid) {
        throw new Error();
      }
    } catch (e) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fix the validation errors',
      });
      throw e;
    }

    try {
      const taskId = this.taskId;
      const loanId = this.loanId;
      if (taskId == null || loanId == null) {
        throw new Error();
      }
      const { annualTaxAmount, propertyId } = this.taxFormStore.getFormValues();
      const property = this.loanProperties.find(
        p => p.propertyId === Number(propertyId)
      );
      yield this.titleCommitmentService.updateTax({
        taskId,
        loanId,
        propertyId: Number(propertyId),
        recordIndex,
        annualTaxAmount: Number(annualTaxAmount),
        propertyAddress: property?.fullAddress ?? '',
      });
      yield this.getReviewTaskData(taskId);
    } catch {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the tax record',
      });
      throw new Error();
    }
  }

}
